// Registro dei pagamenti di un cliente: cosa è atteso (dai pacchetti) e cosa è
// stato registrato (routes/pagamenti_cliente.py). Lo usano la sezione Pagamenti
// della scheda cliente e la modale di registrazione rapida della lista clienti:
// righe, form e regole sono gli stessi, così i due punti non possono divergere.
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, CalendarClock, CalendarOff, Check, HandCoins, CalendarDays, CheckCircle2, FileCheck2, CreditCard, ExternalLink, Loader2, Minus, Pencil, Plus, ShieldAlert, Trash2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogCancel,
} from "./ui/alert-dialog";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { apiFetch } from "../utils/api";
import { MesiArretrati, avvisaRegistrato } from "./PagamentiArretrati";
import { formatCurrency, formatNumber } from "../utils/formatters";
import {
  SECONDARY_BTN, PRIMARY_BTN, PRIMARY_BTN_SM, GHOST_BTN, FIELD_CLS, DIALOG_CONTENT_CLS,
  SectionCard, StatBlock, MonthPicker, DatePicker, InfoTooltip, InfoHint, NumberField,
  GHOST_ACCENT_BTN, ICON_TILES, Badge, DANGER_BTN,
} from "./designSystem";

// Coriandoli dell'animazione "mese incassato": pezzi fissi (non random a ogni
// render) con direzione e rotazione passate come variabili CSS al keyframe
// pagamentiCoriandolo in index.css. Colori solo dalla palette del design system.
const CORIANDOLI = Array.from({ length: 22 }, (_, i) => {
  const angolo = (i / 22) * Math.PI * 2 + (i % 3) * 0.35;
  const distanza = 70 + (i * 37) % 70;
  return {
    dx: Math.round(Math.cos(angolo) * distanza * 1.6),
    dy: Math.round(Math.sin(angolo) * distanza - 30),
    rot: ((i * 83) % 360) - 180,
    colore: ["#5b5bd6", "#12a05c", "#f59e0b", "#0d8377", "#c2570f"][i % 5],
    forma: i % 3 === 0 ? "9999px" : "2px",
    ritardo: (i % 6) * 25,
  };
});

function CoriandoliPagamenti() {
  return (
    <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center" aria-hidden="true">
      {CORIANDOLI.map((c, i) => (
        <span
          key={i}
          className="pagamenti-coriandolo absolute w-2 h-3"
          style={{
            background: c.colore,
            borderRadius: c.forma,
            "--dx": `${c.dx}px`,
            "--dy": `${c.dy}px`,
            "--rot": `${c.rot}deg`,
            animationDelay: `${c.ritardo}ms`,
          }}
        />
      ))}
    </div>
  );
}

// Stesso perimetro di routes/pagamenti_cliente.py (_puo_registrare_pagamenti):
// il pagamento è dato amministrativo. Il commerciale resta fuori anche perché il
// suo compenso di iscrizione matura con il pagamento dell'attivazione.
export function puoRegistrarePagamenti(user) {
  const ruoli = [user?.role, ...(user?.secondary_roles || [])];
  return user?.email === "info@diecixcento.com" || !!user?.is_responsabile_operativo
    || ruoli.includes("amm") || ruoli.includes("direttore_commerciale");
}

// Stesso perimetro di routes/scadenze_pagamenti.py (_puo_impostare_scadenza):
// la scadenza è un termine di fatturazione, la sceglie solo l'amministrazione.
export function puoImpostareScadenza(user) {
  const ruoli = [user?.role, ...(user?.secondary_roles || [])];
  return user?.email === "info@diecixcento.com" || !!user?.is_responsabile_operativo || ruoli.includes("amm");
}

// Stesso perimetro di routes/pagamenti_cliente.py (_puo_saltare_mensili): admin
// e amministrazione. Saltare un mese toglie fatturato all'agenzia, quindi è
// più stretto di chi registra i pagamenti.
export function puoSaltareMensili(user) {
  const ruoli = [user?.role, ...(user?.secondary_roles || [])];
  return user?.email === "info@diecixcento.com" || !!user?.is_responsabile_operativo || ruoli.includes("amm");
}

const nomeMeseLungo = (ym) => new Date(`${ym}-01T00:00:00`).toLocaleDateString("it-IT", { month: "long", year: "numeric" });

// Stesso perimetro di routes/fatturato_percentuale.py (_puo_modificare): chi
// inserisce il fatturato del cliente può anche cambiarne la percentuale.
export function puoModificarePct(user) {
  const ruoli = [user?.role, ...(user?.secondary_roles || [])];
  return user?.email === "info@diecixcento.com" || !!user?.is_responsabile_operativo
    || ["direttore_tecnico", "direttore_commerciale", "pm", "amm"].some(r => ruoli.includes(r));
}

// Percentuale applicata al fatturato di `mese` (stepper della riga Canone).
// Risponde col registro del fatturato e lo stato pagamenti ricalcolato.
export async function salvaPctMese(clientId, mese, pct) {
  const res = await apiFetch(`/clients/${clientId}/fatturato-percentuale/${mese}/pct`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ pct }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    toast.error(body.detail || "Errore nel salvataggio della percentuale");
    throw new Error(body.detail || "pct");
  }
  return body;
}

const PASSO_PCT = 0.5;

// Stepper − X% + accanto all'importo del canone. Il valore cambia subito e si
// salva mezzo secondo dopo l'ultimo tocco: tre clic di fila sono un solo
// salvataggio e un solo ricalcolo, non tre richieste in gara fra loro.
function StepperPercentuale({ quota, onCambia }) {
  const [valore, setValore] = useState(quota.pct);
  const [saving, setSaving] = useState(false);
  const timer = useRef(null);

  useEffect(() => { setValore(quota.pct); }, [quota.pct]);
  useEffect(() => () => clearTimeout(timer.current), []);

  const imposta = (nuovo) => {
    nuovo = Math.min(100, Math.max(0, Math.round(nuovo * 100) / 100));
    if (nuovo === valore) return;
    setValore(nuovo);
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await onCambia(nuovo);
      } catch {
        setValore(quota.pct);
      } finally {
        setSaving(false);
      }
    }, 500);
  };

  const diverso = valore !== quota.pct_contratto;
  const tasto = "w-7 h-full flex items-center justify-center text-[#9095a1] hover:text-[#5b5bd6] hover:bg-[#f1f1f8] transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#9095a1]";
  return (
    <div className="inline-flex items-center gap-1.5" data-testid="canone-pct-stepper">
      <div className={`h-7 inline-flex items-stretch rounded-[9px] border bg-white overflow-hidden ${diverso ? "border-[#c9c9f0]" : "border-[#e0e0ea]"}`}>
        <button type="button" className={tasto} onClick={() => imposta(valore - PASSO_PCT)} disabled={valore <= 0} aria-label="Diminuisci percentuale" data-testid="canone-pct-meno">
          <Minus className="w-3.5 h-3.5" />
        </button>
        <InfoTooltip
          title="Percentuale sul fatturato"
          description={`Vale solo sul fatturato di ${new Date(quota.mese_fatturato + "-01").toLocaleDateString("it-IT", { month: "long" })}: i mesi seguenti ripartono dal ${formatNumber(quota.pct_contratto)}% del contratto.`}
        >
          <span
            className={`min-w-[52px] px-1 flex items-center justify-center gap-1 text-[12.5px] font-semibold scheda-font-mono border-x ${diverso ? "text-[#5b5bd6] bg-[#f6f6fe] border-[#c9c9f0]" : "text-[#15161c] border-[#e0e0ea]"}`}
            data-testid="canone-pct-valore"
          >
            {saving && <Loader2 className="w-3 h-3 animate-spin" />}
            {formatNumber(valore)}%
          </span>
        </InfoTooltip>
        <button type="button" className={tasto} onClick={() => imposta(valore + PASSO_PCT)} disabled={valore >= 100} aria-label="Aumenta percentuale" data-testid="canone-pct-piu">
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>
      {diverso && (
        <button type="button" className="text-[11.5px] font-semibold text-[#9095a1] hover:text-[#5b5bd6] transition-colors scheda-font-ui" onClick={() => imposta(quota.pct_contratto)} data-testid="canone-pct-ripristina">
          Contratto {formatNumber(quota.pct_contratto)}%
        </button>
      )}
    </div>
  );
}

// Scadenza del mese (y, m 0-based) col giorno scelto; un giorno che il mese non
// ha diventa l'ultimo, come in scadenza_del_mese del backend.
const scadenzaNelMese = (y, m, giorno) => new Date(y, m, Math.min(giorno, new Date(y, m + 1, 0).getDate()));

export function prossimaScadenza(dataScadenza) {
  if (!dataScadenza) return null;
  const [y, m, d] = dataScadenza.split("-").map(Number);
  const oggi = new Date();
  oggi.setHours(0, 0, 0, 0);
  const inizio = new Date(y, m - 1, d);
  if (inizio >= oggi) return inizio;
  const questoMese = scadenzaNelMese(oggi.getFullYear(), oggi.getMonth(), d);
  return questoMese >= oggi ? questoMese : scadenzaNelMese(oggi.getFullYear(), oggi.getMonth() + 1, d);
}

const DESCRIZIONE_SOLLECITI =
  "Se un pagamento mensile non risulta registrato alla scadenza parte un sollecito a scalare: "
  + "dopo 1 giorno al PM di riferimento, dopo 3 alla direzione tecnica, dopo 5 al commerciale di riferimento, "
  + "dopo 7 alla direzione commerciale, dopo 10 all'amministrazione. Registrare il pagamento ferma i solleciti successivi.";

// Tessera della testata Pagamenti (scadenza, fattura del mese): etichetta e
// stato in alto, il dato in grande, le azioni in fondo. Due tessere affiancate
// invece di due righe-form: il dato si legge a colpo d'occhio e i campi data
// compaiono solo quando si tocca un'azione.
function TesseraPagamenti({ icon: Icon, tile, etichetta, info, badge, valore, dettaglio, azioni, testId }) {
  return (
    <div className="min-w-0 flex flex-col gap-3 p-4 rounded-[14px] border border-[#e9e9f1] bg-white" data-testid={testId}>
      <div className="flex items-center gap-2.5">
        <div className="w-[30px] h-[30px] rounded-[9px] flex items-center justify-center shrink-0 transition-colors duration-300" style={{ background: tile.bg }}>
          <Icon className="w-[15px] h-[15px] transition-colors duration-300" style={{ color: tile.color }} strokeWidth={1.9} />
        </div>
        <span className="text-[10.5px] font-semibold uppercase tracking-[.06em] text-[#9095a1] inline-flex items-center gap-1 min-w-0">
          <span className="truncate">{etichetta}</span>
          {info}
        </span>
        <div className="flex-1" />
        {badge}
      </div>
      <div className="flex flex-col gap-0.5">
        <span className="text-[19px] font-bold tracking-[-.01em] text-[#15161c]">{valore}</span>
        <span className="text-[12.5px] text-[#6b7280]">{dettaglio}</span>
      </div>
      {azioni && <div className="flex items-center gap-1.5 flex-wrap pt-3 border-t border-[#f0f0f6]">{azioni}</div>}
    </div>
  );
}

function ScadenzaPagamenti({ client, clientId, user, onUpdate }) {
  const canEdit = puoImpostareScadenza(user);
  const valore = client?.data_scadenza_pagamenti || "";
  const [saving, setSaving] = useState(false);
  const prossima = prossimaScadenza(valore);

  const salva = async (nuova) => {
    setSaving(true);
    try {
      const res = await apiFetch(`/clients/${clientId}/scadenza-pagamenti`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data_scadenza_pagamenti: nuova || null }),
      });
      if (res.ok) {
        toast.success(nuova ? "Scadenza pagamenti salvata" : "Scadenza pagamenti rimossa");
        onUpdate();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Errore nel salvataggio della scadenza");
      }
    } finally {
      setSaving(false);
    }
  };

  if (!canEdit && !valore) return null;
  return (
    <TesseraPagamenti
      icon={CalendarClock}
      tile={valore ? ICON_TILES.viola : ICON_TILES.neutro}
      etichetta="Scadenza pagamenti"
      info={<InfoHint title="Scadenza di attivazione e mensili" description={DESCRIZIONE_SOLLECITI} testId="scadenza-pagamenti-info" />}
      badge={valore ? <Badge tone="accent" dot>Solleciti attivi</Badge> : null}
      valore={valore
        ? <>Ogni <span className="scheda-font-mono">{Number(valore.slice(8, 10))}</span> del mese</>
        : <span className="text-[#9095a1]">Non impostata</span>}
      dettaglio={valore
        ? <>Prossima scadenza <span className="scheda-font-mono text-[#3c414d]">{prossima.toLocaleDateString("it-IT")}</span></>
        : "Senza scadenza non parte nessun sollecito automatico"}
      azioni={canEdit && (
        // "Cancella" del calendario passa "" e rimuove la scadenza.
        <DatePicker
          value={valore}
          onChange={(v) => v !== valore && salva(v)}
          renderTrigger={({ toggle }) => (
            <button type="button" className={GHOST_ACCENT_BTN} onClick={toggle} disabled={saving} data-testid="scadenza-pagamenti-data">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CalendarDays className="w-3.5 h-3.5" />}
              {valore ? "Cambia scadenza" : "Imposta scadenza"}
            </button>
          )}
        />
      )}
      testId="scadenza-pagamenti"
    />
  );
}

// Fattura del mese corrente (routes/scadenze_pagamenti.py): il backend decide
// il mese, il client manda solo la data o null per togliere il segno.
// Restituisce la fattura salvata (o null), undefined se il salvataggio fallisce.
export async function salvaFatturaEmessa(clientId, dataEmissione) {
  const res = await apiFetch(`/clients/${clientId}/fattura-emessa`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ data_emissione: dataEmissione || null }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    toast.error(err.detail || "Errore nel salvataggio della fattura");
    return undefined;
  }
  const dati = await res.json();
  return dati.fattura_mese;
}

// Data di oggi in "YYYY-MM-DD" locale: toISOString darebbe il giorno UTC.
export function oggiIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function FatturaMese({ client, clientId, user, meseBackend, onUpdate }) {
  const canEdit = puoImpostareScadenza(user);
  const fattura = client?.fattura_mese;
  const [saving, setSaving] = useState(false);
  const nomeMese = new Date(`${meseBackend || new Date().toISOString().slice(0, 7)}-01T00:00:00`)
    .toLocaleDateString("it-IT", { month: "long", year: "numeric" });

  const salva = async (data) => {
    setSaving(true);
    try {
      const salvata = await salvaFatturaEmessa(clientId, data);
      if (salvata !== undefined) {
        toast.success(data ? "Fattura segnata come emessa" : "Fattura segnata da emettere");
        onUpdate();
      }
    } finally {
      setSaving(false);
    }
  };

  const dataIt = fattura ? new Date(`${fattura.data}T00:00:00`).toLocaleDateString("it-IT", { day: "numeric", month: "long" }) : null;
  const scegliData = (etichetta, testId) => (
    // Dal calendario si sceglie o corregge la data; "Cancella" riporta a "da emettere".
    <DatePicker
      value={fattura?.data || ""}
      onChange={(v) => v !== (fattura?.data || "") && salva(v)}
      renderTrigger={({ toggle }) => (
        <button type="button" className={GHOST_ACCENT_BTN} onClick={toggle} disabled={saving} data-testid={testId}>
          {fattura ? <Pencil className="w-3.5 h-3.5" /> : <CalendarDays className="w-3.5 h-3.5" />}
          {etichetta}
        </button>
      )}
    />
  );

  return (
    <TesseraPagamenti
      icon={FileCheck2}
      tile={fattura ? ICON_TILES.verde : ICON_TILES.arancio}
      etichetta={`Fattura di ${nomeMese}`}
      info={(
        <InfoHint
          title="Fattura del mese"
          description="L'amministrazione segna qui se ha emesso la fattura del mese corrente e in che data. Si può segnare anche dalla lista clienti. A inizio mese il segno riparte da zero."
          testId="fattura-mese-info"
        />
      )}
      badge={fattura ? <Badge tone="success" dot>Fatturato</Badge> : <Badge tone="warning" dot>Da fatturare</Badge>}
      valore={fattura
        ? <span className="text-[#0f7040]">Emessa il {dataIt}</span>
        : "Non ancora emessa"}
      dettaglio={fattura
        ? <>Data di emissione <span className="scheda-font-mono text-[#3c414d]">{fattura.data.split("-").reverse().join("/")}</span></>
        : "Segnala appena la fattura del mese è partita"}
      azioni={canEdit && (fattura
        ? scegliData("Cambia data", "fattura-mese-data")
        : (
          <>
            <button type="button" className={PRIMARY_BTN_SM} onClick={() => salva(oggiIso())} disabled={saving} data-testid="fattura-mese-oggi">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              Emessa oggi
            </button>
            {scegliData("Altra data", "fattura-mese-data")}
          </>
        ))}
      testId="fattura-mese"
    />
  );
}

// Voci del canone mensile ({ addon, ... }): gli addon a importo fisso si
// sommano, quelli a percentuale si accodano. `quota` è la quota a percentuale
// del mese (stato_pagamenti.mese_corrente.quota_percentuale): quando il
// fatturato del mese prima è inserito ha un importo e si somma anche lei.
const sommaImportiFissi = (voci) =>
  voci.reduce((sum, v) => sum + (v.addon.tipo_valore === "importo" ? Number(v.addon.valore || 0) : 0), 0);

const quotaNota = (voci, quota) =>
  quota?.importo != null && voci.some(v => v.addon.tipo_valore === "percentuale") ? quota.importo : null;

function descriviImportoMensili(voci, quota) {
  const fissi = voci.filter(v => v.addon.tipo_valore === "importo");
  const parti = fissi.length ? [formatCurrency(sommaImportiFissi(voci), { decimals: 0 })] : [];
  const nota = quotaNota(voci, quota);
  if (nota != null) {
    parti.push(`${formatCurrency(nota, { decimals: 0 })} (${formatNumber(quota.pct)}% di ${formatCurrency(quota.fatturato, { decimals: 0 })})`);
  } else {
    voci.filter(v => v.addon.tipo_valore !== "importo").forEach(v => parti.push(`${formatNumber(v.addon.valore)}%`));
  }
  return `${parti.join(" + ")}/mese`;
}

// Canone del cliente dai soli servizi attaccati: somma degli addon mensili di
// tutti i pacchetti. Sostituisce `importo_totale` / `importo_mensile`, scritti
// dalle vecchie tag di reparto e dall'import Excel e mai aggiornati dai
// servizi. È il prezzo pattuito, anche nei mesi in cui un mensile non è ancora
// dovuto. La lista clienti riceve il documento grezzo: se `pacchetti` manca si
// ricade sul vecchio formato a pacchetto singolo (backend/pacchetti.py).
export function canoneServizi(client) {
  const pacchetti = client?.pacchetti ?? (client?.pacchetto_addons ? [{ addons: client.pacchetto_addons }] : []);
  const mensili = pacchetti.flatMap(pk => pk.addons || []).filter(a => a.ricorrenza === "mensile");
  const fisso = mensili.filter(a => a.tipo_valore === "importo").reduce((s, a) => s + Number(a.valore || 0), 0);
  const percentuale = mensili.filter(a => a.tipo_valore === "percentuale").reduce((s, a) => s + Number(a.valore || 0), 0);
  return { fisso, percentuale, haMensili: mensili.length > 0 };
}

// Storico: i record di una registrazione accorpata (stesso fattura_id, vedi
// routes/pagamenti_cliente.py) sono una sola fattura e una sola riga.
function raggruppaPerFattura(pagamenti) {
  const righe = [];
  const perFattura = {};
  pagamenti.forEach(p => {
    if (!p.fattura_id) {
      righe.push({ ...p, voci: null });
      return;
    }
    if (!perFattura[p.fattura_id]) {
      perFattura[p.fattura_id] = { ...p, label: "Canone mensile", importo: p.importo_fattura ?? 0, voci: [] };
      righe.push(perFattura[p.fattura_id]);
    }
    perFattura[p.fattura_id].voci.push(p.label);
  });
  return righe;
}

// Righe attese del cliente e loro stato nel mese: un gruppo per pacchetto
// (attivazione e addon una tantum) più il canone mensile accorpato.
export function modelloPagamenti(client, pagamenti, meseBackend, quota = null) {
  // Il mese lo decide il backend (stato_pagamenti.py), come per l'etichetta
  // Pagato e gli arretrati: così scheda e lista non possono divergere a cavallo
  // di mezzanotte o con un mese simulato in locale.
  const meseCorrente = meseBackend || new Date().toISOString().slice(0, 7);

  // Un gruppo di righe per pacchetto: la sua attivazione e i suoi addon.
  // Il pacchetto del formato vecchio ha id "pacchetto_legacy" e le sue
  // attivazioni registrate prima non hanno pacchetto_id (backend/pacchetti.py).
  const pacchetti = client?.pacchetti || [];
  const multiPacchetto = pacchetti.length > 1;
  const pacchettoDelPagamento = (p) => p.pacchetto_id || "pacchetto_legacy";

  const mensili = [];
  const righePacchetto = (pk) => {
    const righe = [];
    const importoAttivazione = pk.importo_attivazione;
    // attivazione_pregressa: contratto già in corso prima dell'app, attivazione
    // incassata fuori dai Pagamenti (backend/routes/contratto_pregresso.py) —
    // niente riga da registrare e addon mensili dovuti da subito.
    const haAttivazione = !pk.attivazione_pregressa && importoAttivazione != null && importoAttivazione > 0;
    const pagamentoAttivazione = haAttivazione
      ? pagamenti.find(p => p.tipo === "attivazione" && pacchettoDelPagamento(p) === pk.pacchetto_id)
      : null;
    // "Primo mese solo attivazione" (opzione del servizio, copiata sul
    // pacchetto): gli addon mensili sono dovuti dal mese successivo a quello
    // in cui è stata pagata l'attivazione. Finché l'attivazione non è pagata
    // non sono ancora dovuti. I pacchetti creati prima dell'opzione non hanno
    // il campo e restano come prima (addon dovuti da subito).
    const meseAttivazione = pagamentoAttivazione ? String(pagamentoAttivazione.data_pagamento).slice(0, 7) : null;
    const addonMensiliNonAncoraDovuti = !!pk.primo_mese_solo_attivazione && haAttivazione
      && (!meseAttivazione || meseCorrente <= meseAttivazione);
    if (haAttivazione) {
      // Servizi eventi con più attivazioni: la prima ha un nome suo
      // (le successive sono addon una tantum, più sotto).
      const nomeAtt = pk.nome_attivazione || "Attivazione";
      const label = multiPacchetto ? `${nomeAtt} — ${pk.nome}` : nomeAtt;
      righe.push({
        // Con un solo pacchetto restano chiave e data-testid di prima.
        key: multiPacchetto ? `attivazione_${pk.pacchetto_id}` : "attivazione",
        testId: multiPacchetto ? `registra-attivazione-btn-${pk.pacchetto_id}` : "registra-attivazione-btn",
        base: { tipo: "attivazione", label, ricorrenza: "una_tantum", pacchetto_id: pk.pacchetto_id },
        label: nomeAtt,
        valueLabel: formatCurrency(importoAttivazione, { decimals: 0 }),
        valoreNumerico: importoAttivazione,
        tipoValore: "importo",
        paid: !!pagamentoAttivazione,
        paidText: pagamentoAttivazione ? `Pagata il ${new Date(pagamentoAttivazione.data_pagamento).toLocaleDateString("it-IT")}` : "",
        pendingText: "In attesa di pagamento",
        suggerito: importoAttivazione,
      });
    }
    (pk.addons || []).forEach((a, i) => {
      const relativi = pagamenti.filter(p => p.tipo === "addon" && p.addon_id === a.addon_id);
      const isMensile = a.ricorrenza === "mensile";
      const valoreLabel = a.tipo_valore === "percentuale" ? `${formatNumber(a.valore)}%` : formatCurrency(a.valore, { decimals: 0 });
      const suggerito = a.tipo_valore === "importo" ? a.valore : "";
      const key = a.addon_id || `addon_${pk.pacchetto_id}_${i}`;
      if (isMensile) {
        // Addon aggiunto dalla scheda: dovuto da `dovuto_dal` in poi
        // (backend/stato_pagamenti.py). Prima non entra nel canone del mese.
        const dal = a.dovuto_dal ? String(a.dovuto_dal).slice(0, 7) : null;
        if (dal && meseCorrente < dal && !relativi.some(p => p.periodo === meseCorrente)) return;
        // I mensili non hanno una riga per addon: finiscono nel canone del
        // mese, una sola registrazione per l'unica fattura (rigaCanone sotto).
        mensili.push({
          addon: a,
          pacchetto: pk,
          pagato: relativi.some(p => p.periodo === meseCorrente),
          nonDovuto: addonMensiliNonAncoraDovuti,
          meseAttivazione,
        });
      } else {
        const pagato = relativi[0];
        righe.push({
          key,
          testId: `pagamento-addon-${key}`,
          base: { tipo: "addon", label: a.label, ricorrenza: "una_tantum", addon_id: a.addon_id },
          label: a.label,
          valueLabel: `${valoreLabel} — una tantum`,
          tipoValore: a.tipo_valore,
          paid: !!pagato,
          paidText: pagato ? `Pagato il ${new Date(pagato.data_pagamento).toLocaleDateString("it-IT")}` : "",
          pendingText: "In attesa di pagamento",
          suggerito,
        });
      }
    });
    return righe;
  };

  const gruppi = pacchetti
    .map(pk => ({ pk, righe: righePacchetto(pk) }))
    .filter(g => g.righe.length > 0);

  // Canone del mese: tutti gli addon mensili di tutti i pacchetti in un'unica
  // riga, perché l'amministrazione emette un'unica fattura. La registrazione
  // accorpata (POST /pagamenti/mensili) prende solo quelli dovuti e non ancora
  // registrati: quelli "dal mese dopo l'attivazione" restano fuori da soli.
  const nomeMeseCorrente = new Date(meseCorrente + "-01").toLocaleDateString("it-IT", { month: "long", year: "numeric" });
  const mensiliDovuti = mensili.filter(m => !m.nonDovuto || m.pagato);
  // Canone del mese saltato dall'amministrazione (mensili_saltati sul cliente):
  // niente da registrare né da incassare, come per backend/stato_pagamenti.py.
  const saltato = client?.mensili_saltati?.[meseCorrente] || null;
  const rigaCanone = (() => {
    if (mensili.length === 0) return null;
    const base = { tipo: "canone", ricorrenza: "mensile" };
    const common = { key: "canone_mensile", testId: "registra-canone-btn", base, label: "Canone mensile", tipoValore: "importo" };
    if (saltato && !mensili.some(m => m.pagato)) {
      return {
        ...common,
        voci: mensili,
        valueLabel: descriviImportoMensili(mensili),
        paid: false,
        nonDovuto: true,
        saltato,
        pendingText: `Saltato ${nomeMeseCorrente}`,
        suggerito: "",
      };
    }
    if (mensiliDovuti.length === 0) {
      const primo = mensili[0];
      return {
        ...common,
        voci: mensili,
        valueLabel: descriviImportoMensili(mensili),
        paid: false,
        nonDovuto: true,
        pendingText: primo.meseAttivazione ? "Dal mese prossimo" : "Dal mese dopo l'attivazione",
        suggerito: "",
      };
    }
    const daPagare = mensiliDovuti.filter(m => !m.pagato);
    return {
      ...common,
      voci: mensiliDovuti,
      valueLabel: descriviImportoMensili(mensiliDovuti, quota),
      // Solo con il fatturato inserito: senza, non c'è una quota da ritoccare.
      quota: quotaNota(mensiliDovuti, quota) != null ? quota : null,
      paid: daPagare.length === 0,
      paidText: `Pagato ${nomeMeseCorrente}`,
      pendingText: daPagare.length < mensiliDovuti.length ? `${daPagare.length} voci da pagare questo mese` : "Da pagare questo mese",
      suggerito: sommaImportiFissi(daPagare) + (quotaNota(daPagare, quota) || 0),
    };
  })();

  const tutteLeRighe = [...gruppi.flatMap(g => g.righe), ...(rigaCanone ? [rigaCanone] : [])];
  const haPacchetto = tutteLeRighe.length > 0;
  // Conteggi e festa solo su ciò che è dovuto questo mese.
  const items = tutteLeRighe.filter(it => !it.nonDovuto);

  const paidCount = items.filter(it => it.paid).length;
  const daIncassare = items.reduce((sum, it) => sum + (!it.paid && it.tipoValore === "importo" ? Number(it.suggerito || 0) : 0), 0);
  const tuttoIncassato = items.length > 0 && paidCount === items.length;

  return { meseCorrente, multiPacchetto, gruppi, rigaCanone, tutteLeRighe, haPacchetto, items, paidCount, daIncassare, tuttoIncassato };
}

// Righe con i bottoni "+ Registra" e il form, coriandoli quando il mese si chiude.
// `rapido` (registrazione dalla lista clienti): le voci con un importo atteso si
// registrano con un tocco, oggi e per il mese corrente; la matita apre il form
// per cambiare importo, mese, data o note. onRegistrato riceve la risposta del
// backend, che porta anche lo stato aggiornato del cliente.
// `onCambiaPct` (pct) => Promise: solo per chi può cambiare la percentuale del
// mese; senza, lo stepper della riga Canone non compare.
export function RegistroPagamenti({ modello, clientId, pagamenti, canRegister, onRegistrato, onCambiaPct, rapido = false }) {
  const { meseCorrente, multiPacchetto, gruppi, rigaCanone, tutteLeRighe, tuttoIncassato } = modello;
  const [registerKey, setRegisterKey] = useState(null);
  const [regForm, setRegForm] = useState({ importo: "", data_pagamento: "", periodo: "", note: "" });
  const [saving, setSaving] = useState(false);
  const [savingKey, setSavingKey] = useState(null);
  // Canone oltre la scadenza del contratto: il backend lo rifiuta finché non
  // si conferma (409 "contratto_scaduto", routes/pagamenti_cliente.py). Qui
  // resta la registrazione da ripetere con la conferma.
  const [fuoriContratto, setFuoriContratto] = useState(null);
  const oggi = new Date().toISOString().slice(0, 10);

  // Festa quando la registrazione appena fatta chiude il mese. Parte solo dopo
  // un submit riuscito in questa sessione: aprire una scheda già tutta pagata, o
  // il passaggio [] → pagamenti al primo caricamento, non devono farla scattare.
  const [festa, setFesta] = useState(false);
  const registratoOra = useRef(false);
  useEffect(() => {
    if (registratoOra.current && tuttoIncassato) setFesta(true);
    registratoOra.current = false;
  }, [tuttoIncassato, pagamenti]);
  useEffect(() => {
    if (!festa) return undefined;
    const t = setTimeout(() => setFesta(false), 3200);
    return () => clearTimeout(t);
  }, [festa]);

  const openForm = (key, defaults) => {
    setRegisterKey(key);
    setRegForm({ importo: "", data_pagamento: oggi, periodo: meseCorrente, note: "", ...defaults });
  };
  const closeForm = () => setRegisterKey(null);

  const registra = async (base, form, key, confermaFuoriContratto = false) => {
    if (form.importo === "" || form.importo == null || isNaN(Number(form.importo)) || Number(form.importo) < 0) {
      toast.error("Inserisci un importo valido");
      return;
    }
    setSaving(true);
    setSavingKey(key);
    try {
      const canone = base.tipo === "canone";
      const body = canone
        ? {
          importo: Number(form.importo),
          periodo: form.periodo || meseCorrente,
          data_pagamento: form.data_pagamento || oggi,
          note: form.note || null,
          fuori_contratto: confermaFuoriContratto,
        }
        : {
          ...base,
          importo: Number(form.importo),
          data_pagamento: form.data_pagamento || oggi,
          note: form.note || null,
          periodo: base.ricorrenza === "mensile" ? (form.periodo || meseCorrente) : null,
        };
      const res = await apiFetch(`/clients/${clientId}/pagamenti${canone ? "/mensili" : ""}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const risposta = await res.json().catch(() => ({}));
        toast.success("Pagamento registrato");
        registratoOra.current = true;
        setFuoriContratto(null);
        closeForm();
        onRegistrato(risposta);
      } else {
        const err = await res.json().catch(() => ({}));
        if (err.detail?.codice === "contratto_scaduto") {
          setFuoriContratto({ base, form, key, scadenza: err.detail.scadenza, periodo: body.periodo });
        } else {
          toast.error(err.detail || "Errore nella registrazione");
        }
      }
    } finally {
      setSaving(false);
      setSavingKey(null);
    }
  };
  const submitForm = (base) => registra(base, regForm, registerKey);

  // Funzione di render, non componente: un componente definito dentro
  // RegistroPagamenti sarebbe un tipo nuovo a ogni render, React lo rimonterebbe
  // a ogni tasto e gli input perderebbero il focus.
  const renderRegisterForm = (base) => (
    <div className="mt-2 p-3 rounded-[12px] bg-[#fbfbfd] border border-[#eeeef5] space-y-2" data-testid={`pagamento-form-${base.addon_id || base.tipo}`}>
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label className="text-[11px] text-[#9095a1]">{base.tipo === "canone" ? "Importo fattura" : "Importo"}</label>
          <div className="mt-1 w-32">
            <NumberField
              value={regForm.importo}
              onChange={(v) => setRegForm(prev => ({ ...prev, importo: v }))}
              testId="pagamento-importo"
            />
          </div>
        </div>
        {base.ricorrenza === "mensile" && (
          <div>
            <label className="text-[11px] text-[#9095a1]">Mese</label>
            <MonthPicker
              value={regForm.periodo}
              onChange={(v) => setRegForm(prev => ({ ...prev, periodo: v }))}
              className={`${FIELD_CLS} h-[38px] mt-1 w-32`}
            />
          </div>
        )}
        <div>
          <label className="text-[11px] text-[#9095a1]">Data pagamento</label>
          <DatePicker
            value={regForm.data_pagamento}
            onChange={(v) => setRegForm(prev => ({ ...prev, data_pagamento: v }))}
            className={`${FIELD_CLS} h-[38px] mt-1 w-36`}
          />
        </div>
        <div className="flex-1 min-w-[140px]">
          <label className="text-[11px] text-[#9095a1]">Note (opzionale)</label>
          <Input
            value={regForm.note}
            onChange={(e) => setRegForm(prev => ({ ...prev, note: e.target.value }))}
            className={`${FIELD_CLS} h-[38px] mt-1`}
          />
        </div>
      </div>
      <div className="flex gap-2">
        <button className={PRIMARY_BTN} style={{ height: 32 }} onClick={() => submitForm(base)} disabled={saving} data-testid="conferma-pagamento-btn">
          <CheckCircle2 className="w-3.5 h-3.5" /> Conferma
        </button>
      </div>
    </div>
  );

  const renderRiga = (it) => {
    const i = tutteLeRighe.indexOf(it);
    return (
      <div
        key={it.key}
        className={festa ? "pagamenti-festa-riga" : undefined}
        style={{ background: it.paid ? "#fbfefc" : "#fff", ...(festa ? { animationDelay: `${i * 110}ms` } : {}) }}
      >
        <div className="flex items-center gap-[14px] px-[18px] py-[15px] flex-wrap">
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: it.paid ? "#12a05c" : it.nonDovuto ? "#c9c9d6" : "#e0a12b" }} />
          <div className="flex-1 min-w-[140px]">
            <div className="text-[14.5px] font-semibold text-[#15161c]">{it.label}</div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-[13px] scheda-font-mono text-[#6b7280]">{it.valueLabel}</div>
              {it.quota && onCambiaPct && <StepperPercentuale quota={it.quota} onCambia={onCambiaPct} />}
            </div>
            {it.voci && (
              <div className="text-[12px] text-[#9095a1] mt-0.5" data-testid="canone-voci">
                {it.voci.map(v => (multiPacchetto ? `${v.addon.label} (${v.pacchetto.nome || "Pacchetto"})` : v.addon.label)).join(" · ")}
              </div>
            )}
          </div>
          {(() => {
            const stato = (
              <span
                className="h-7 px-[11px] rounded-full text-[12.5px] font-semibold shrink-0 inline-flex items-center"
                style={it.paid
                  ? { background: "#e7f7ee", color: "#0f7040" }
                  : it.nonDovuto ? { background: "#f1f1f6", color: "#6b7280" } : { background: "#fdf3dd", color: "#8a6414" }}
              >
                {it.paid ? it.paidText : it.pendingText}
              </span>
            );
            if (it.saltato) {
              return (
                <InfoTooltip
                  title="Mensile saltato"
                  description={`Saltato da ${it.saltato.by_name || "amministrazione"} il ${new Date(it.saltato.at).toLocaleDateString("it-IT")}: ${it.saltato.motivo}`}
                >
                  {stato}
                </InfoTooltip>
              );
            }
            return it.nonDovuto ? (
              <InfoTooltip title="Non ancora dovuta" description="Il servizio prevede che il primo mese si paghi solo l'attivazione.">
                {stato}
              </InfoTooltip>
            ) : stato;
          })()}
          {canRegister && !it.paid && !it.nonDovuto && (rapido && Number(it.suggerito) > 0 && registerKey !== it.key ? (
            <div className="flex items-center gap-1.5">
              <button
                className={PRIMARY_BTN}
                style={{ height: 34 }}
                onClick={() => registra(it.base, { importo: it.suggerito, data_pagamento: oggi, periodo: meseCorrente, note: "" }, it.key)}
                disabled={saving}
                data-testid={`${it.testId}-rapido`}
              >
                {savingKey === it.key
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <CheckCircle2 className="w-3.5 h-3.5" />}
                Registra {formatCurrency(it.suggerito, { decimals: 0 })}
              </button>
              <InfoTooltip title="Registra con altri dati" description="Importo diverso, mese, data del pagamento o note.">
                <button
                  className={`${SECONDARY_BTN} !px-0 w-[34px] justify-center`}
                  style={{ height: 34 }}
                  onClick={() => openForm(it.key, { importo: it.suggerito })}
                  disabled={saving}
                  aria-label="Registra con altri dati"
                  data-testid={it.testId}
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              </InfoTooltip>
            </div>
          ) : (
            <button
              className={SECONDARY_BTN}
              style={{ height: 34 }}
              onClick={() => registerKey === it.key ? closeForm() : openForm(it.key, { importo: it.suggerito })}
              data-testid={it.testId}
            >
              {registerKey === it.key ? "Annulla" : "+ Registra"}
            </button>
          ))}
        </div>
        {registerKey === it.key && (
          <div className="px-[18px] pb-[15px]">
            {renderRegisterForm(it.base)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="relative">
      {festa && <CoriandoliPagamenti />}
      <div className="rounded-[14px] border border-[#e9e9f1] overflow-hidden divide-y divide-[#e9e9f1]">
        {gruppi.map(({ pk, righe }) => (
          <div key={pk.pacchetto_id} className="divide-y divide-[#e9e9f1]" data-testid={`pagamenti-pacchetto-${pk.pacchetto_id}`}>
            {multiPacchetto && (
              <div className="px-[18px] py-2 bg-[#f8f8fc] text-[10.5px] font-bold uppercase tracking-[.08em] text-[#9095a1]">
                {pk.nome || "Pacchetto"}
              </div>
            )}
            {righe.map(renderRiga)}
          </div>
        ))}
        {rigaCanone && (
          <div data-testid="pagamenti-canone">
            {multiPacchetto && (
              <div className="px-[18px] py-2 bg-[#f8f8fc] text-[10.5px] font-bold uppercase tracking-[.08em] text-[#9095a1]">
                Mensili · un&apos;unica fattura
              </div>
            )}
            {renderRiga(rigaCanone)}
          </div>
        )}
      </div>
      {tuttoIncassato && (
        <div
          className={`mt-3 flex items-center gap-2 px-3.5 py-2.5 rounded-[12px] bg-[#e7f7ee] text-[#0f7040] text-[13px] font-semibold ${festa ? "pagamenti-festa-banner" : ""}`}
          data-testid="pagamenti-tutto-incassato"
        >
          <CheckCircle2 className="w-4 h-4 shrink-0" strokeWidth={2.2} />
          Tutti i pagamenti del mese sono incassati
        </div>
      )}

      <AlertDialog open={!!fuoriContratto} onOpenChange={(o) => { if (!o && !saving) setFuoriContratto(null); }}>
        <AlertDialogContent className={DIALOG_CONTENT_CLS} data-testid="pagamento-fuori-contratto-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#15161c] flex items-center gap-2">
              <AlertTriangle className="w-[18px] h-[18px] text-[#c0392b] shrink-0" strokeWidth={2.2} />
              Contratto scaduto
            </AlertDialogTitle>
            <AlertDialogDescription>
              {fuoriContratto?.scadenza
                ? `Il contratto è scaduto il ${new Date(fuoriContratto.scadenza).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}`
                : "Il contratto è scaduto"}
              {" "}e il rinnovo non è ancora firmato. Il canone di {fuoriContratto ? nomeMeseLungo(fuoriContratto.periodo) : ""} verrà
              registrato come incasso fuori contratto: il cliente resta da rinnovare.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Annulla</AlertDialogCancel>
            <button
              className={PRIMARY_BTN}
              onClick={() => registra(fuoriContratto.base, fuoriContratto.form, fuoriContratto.key, true)}
              disabled={saving}
              data-testid="conferma-pagamento-fuori-contratto-btn"
            >
              {saving ? "Salvo..." : "Registra comunque"}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Mesi passati da registrare, sopra le righe del mese in corso: l'avviso
// arretrati in cima alla scheda porta qui, e da qui si registrano senza dover
// cambiare il mese nel form.
function BloccoArretrati({ arretrati, clientId, canRegister, onRegistrato }) {
  if (!arretrati?.length) return null;
  return (
    <div className="mb-3 rounded-[14px] border border-[#f6c9c3] overflow-hidden" data-testid="pagamenti-arretrati">
      <div className="px-4 py-2.5 bg-[#fef6f5] flex items-center gap-2 text-[12.5px] font-semibold text-[#8f2a1f]">
        <AlertTriangle className="w-3.5 h-3.5 text-[#c0392b]" strokeWidth={2.2} />
        {arretrati.length === 1 ? "Mese arretrato da registrare" : `${arretrati.length} mesi arretrati da registrare`}
      </div>
      <div className="bg-white">
        <MesiArretrati mesi={arretrati} clientId={clientId} canRegister={canRegister} onRegistrato={onRegistrato} />
      </div>
    </div>
  );
}

// `onPctCambiata`: chi mostra altrove il fatturato percentuale (la sezione della
// scheda) lo rilegge quando la percentuale del mese cambia da qui.
// Il cliente paga senza fattura (backend/routes/commerciale_cliente.py). Il
// backend manda senza_fattura solo al commerciale di riferimento e a PM/AMM/
// direzione: per gli altri il campo manca e la pill non compare. Per scelta
// del cliente niente etichetta, niente tooltip né hover e nessun messaggio al
// cambio di stato: lo stato si legge solo dalla pill. Spenta è una pill grigia
// tratteggiata con l'icona sbiadita (da attivare), accesa una pill arancio
// piena con icona bianca e spunta.
function SenzaFatturaPill({ client, clientId, onUpdate }) {
  const [saving, setSaving] = useState(false);
  if (!client || !("senza_fattura" in client)) return null;
  const attivo = !!client.senza_fattura;

  const toggle = async () => {
    setSaving(true);
    try {
      const res = await apiFetch(`/clients/${clientId}/senza-fattura`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ senza_fattura: !attivo }),
      });
      if (res.ok) {
        onUpdate();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Errore nel salvataggio");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={saving}
      aria-pressed={attivo}
      aria-label="Paga senza fattura"
      className={`relative inline-flex items-center justify-center h-8 px-3 rounded-full border-[1.5px] transition-colors disabled:opacity-60 focus-visible:outline-none focus-visible:shadow-[0_0_0_3px_rgba(91,91,214,.14)] ${attivo
        ? "border-transparent text-white shadow-[0_3px_10px_rgba(194,87,15,.30)]"
        : "border-dashed border-[#c9ccd6] bg-white text-[#b4b8c5]"}`}
      style={attivo ? { background: ICON_TILES.arancio.color } : undefined}
      data-testid="senza-fattura-toggle"
    >
      <HandCoins className="w-[18px] h-[18px]" strokeWidth={2.1} />
      {/* La spunta nell'angolo rende lo stato acceso leggibile anche senza
          distinguere i colori. */}
      {attivo && (
        <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-white shadow-[0_1px_3px_rgba(15,23,42,.25)]">
          <Check className="w-2.5 h-2.5" strokeWidth={3.5} style={{ color: ICON_TILES.arancio.color }} />
        </span>
      )}
    </button>
  );
}

// Salta del tutto il canone di un mese (PUT .../pagamenti/mensili/{mese}/saltato).
// È un mese regalato: esce dal fatturato ma le commissioni del team restano, quindi il
// dialog chiede il motivo e una conferma esplicita prima di abilitare il tasto.
function SaltaMensileDialog({ open, onOpenChange, client, clientId, meseIniziale, onSaltato }) {
  const [mese, setMese] = useState(meseIniziale);
  const [motivo, setMotivo] = useState("");
  const [confermato, setConfermato] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setMese(meseIniziale);
      setMotivo("");
      setConfermato(false);
    }
  }, [open, meseIniziale]);

  const giaSaltato = !!client?.mensili_saltati?.[mese];
  const pronto = !!mese && motivo.trim().length >= 3 && confermato && !giaSaltato;

  const conferma = async () => {
    setSaving(true);
    try {
      const res = await apiFetch(`/clients/${clientId}/pagamenti/mensili/${mese}/saltato`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ motivo: motivo.trim() }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(`Mensile di ${nomeMeseLungo(mese)} saltato`);
        onOpenChange(false);
        onSaltato(body);
      } else {
        toast.error(body.detail || "Impossibile saltare il mensile");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!saving) onOpenChange(o); }}>
      <AlertDialogContent className={DIALOG_CONTENT_CLS} data-testid="salta-mensile-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[#15161c]">Saltare il mensile di {client?.name}?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="flex flex-col gap-3 text-left">
              <div className="flex gap-2.5 p-3 rounded-[12px] border border-[#f6c9c3] bg-[#fef6f5] text-[12.5px] leading-[1.5] text-[#8f2a1f]" data-testid="salta-mensile-avviso">
                <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5 text-[#c0392b]" strokeWidth={2.2} />
                <div className="flex flex-col gap-1">
                  <span className="font-semibold">Operazione con effetti economici</span>
                  <span>
                    Il canone del mese scelto non sarà più dovuto: nessuna fattura attesa, nessun sollecito, nessun arretrato.
                    Il mese esce dal fatturato e dall'incassato, ma PM, commerciale e direzioni ricevono comunque la loro commissione sul canone. Attivazioni e una tantum restano dovute.
                  </span>
                  <span>L&apos;operazione resta registrata con il tuo nome e il motivo.</span>
                </div>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="flex flex-col gap-3">
          <div>
            <label className="text-[11px] text-[#9095a1]">Mese da saltare</label>
            <MonthPicker value={mese} onChange={setMese} className={`${FIELD_CLS} h-[38px] mt-1 w-44`} />
            {giaSaltato && <p className="mt-1 text-[12px] text-[#c0392b]">Questo mese è già saltato.</p>}
          </div>
          <div>
            <label className="text-[11px] text-[#9095a1]">Motivo (obbligatorio)</label>
            <Textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Es. mese omaggio concordato con il cliente"
              className="mt-1 min-h-[72px] rounded-[10px] border-[#e4e4ed] text-[13.5px] focus-visible:ring-0 focus:border-[#5b5bd6]"
              maxLength={500}
              data-testid="salta-mensile-motivo"
            />
          </div>
          <label className="flex items-start gap-2 text-[12.5px] text-[#3c414d] cursor-pointer">
            <Checkbox checked={confermato} onCheckedChange={(v) => setConfermato(!!v)} className="mt-0.5" data-testid="salta-mensile-conferma-check" />
            <span>Confermo che {client?.name} non pagherà il canone di {mese ? nomeMeseLungo(mese) : "questo mese"}.</span>
          </label>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={saving}>Annulla</AlertDialogCancel>
          <button
            className="h-9 px-4 rounded-[10px] bg-[#c0392b] hover:bg-[#a93226] text-white text-[13px] font-semibold transition-colors disabled:bg-[#e8b4ad] disabled:cursor-not-allowed scheda-font-ui inline-flex items-center justify-center gap-1.5"
            onClick={conferma}
            disabled={!pronto || saving}
            data-testid="salta-mensile-conferma-btn"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CalendarOff className="w-3.5 h-3.5" />}
            Salta il mensile
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// Mesi col canone saltato, con chi l'ha deciso e perché. Ripristinare rimette
// il canone fra il dovuto (e fra gli arretrati, se il mese è passato).
function MesiSaltati({ client, clientId, canSkip, onUpdate }) {
  const [daRipristinare, setDaRipristinare] = useState(null);
  const [saving, setSaving] = useState(false);
  const mesi = Object.entries(client?.mensili_saltati || {}).sort(([a], [b]) => b.localeCompare(a));
  if (!mesi.length) return null;

  const ripristina = async () => {
    setSaving(true);
    try {
      const res = await apiFetch(`/clients/${clientId}/pagamenti/mensili/${daRipristinare}/saltato`, { method: "DELETE" });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(`Mensile di ${nomeMeseLungo(daRipristinare)} di nuovo dovuto`);
        setDaRipristinare(null);
        onUpdate();
      } else {
        toast.error(body.detail || "Impossibile ripristinare il mensile");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 rounded-[14px] border border-[#e9e9f1] overflow-hidden" data-testid="mesi-saltati">
      <div className="px-4 py-2.5 bg-[#f8f8fc] flex items-center gap-2 text-[10.5px] font-bold uppercase tracking-[.08em] text-[#9095a1]">
        <CalendarOff className="w-3.5 h-3.5" strokeWidth={2.2} />
        Mensili saltati
      </div>
      <div className="divide-y divide-[#f0f0f6] bg-white">
        {mesi.map(([mese, info]) => (
          <div key={mese} className="flex items-center gap-3 px-4 py-2.5 flex-wrap" data-testid={`mese-saltato-${mese}`}>
            <div className="flex-1 min-w-[180px]">
              <div className="text-[13.5px] font-semibold text-[#15161c] first-letter:uppercase">{nomeMeseLungo(mese)}</div>
              <div className="text-[12px] text-[#6b7280]">
                {info.motivo} · {info.by_name || "amministrazione"}, {new Date(info.at).toLocaleDateString("it-IT")}
              </div>
            </div>
            {canSkip && (
              <button className={SECONDARY_BTN} onClick={() => setDaRipristinare(mese)} data-testid={`ripristina-mese-saltato-${mese}`}>
                <Undo2 className="w-3.5 h-3.5" /> Ripristina
              </button>
            )}
          </div>
        ))}
      </div>

      <AlertDialog open={!!daRipristinare} onOpenChange={(o) => { if (!o && !saving) setDaRipristinare(null); }}>
        <AlertDialogContent className={DIALOG_CONTENT_CLS} data-testid="ripristina-mese-saltato-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#15161c]">Ripristinare il mensile di {daRipristinare ? nomeMeseLungo(daRipristinare) : ""}?</AlertDialogTitle>
            <AlertDialogDescription>
              Il canone torna dovuto: rientra nel fatturato e, se il mese è già passato, compare fra gli arretrati da registrare.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Annulla</AlertDialogCancel>
            <button className={PRIMARY_BTN} onClick={ripristina} disabled={saving} data-testid="conferma-ripristina-mese-saltato-btn">
              {saving ? "Salvo..." : "Ripristina"}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function PagamentiSection({ client, clientId, user, pagamenti, arretrati, meseBackend, quotaPercentuale, onUpdate, onPctCambiata }) {
  const canRegister = puoRegistrarePagamenti(user);
  const canSkip = puoSaltareMensili(user);
  const [showSalta, setShowSalta] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [showStorico, setShowStorico] = useState(false);
  const modello = modelloPagamenti(client, pagamenti, meseBackend, quotaPercentuale);
  const { haPacchetto, paidCount, items, daIncassare, tuttoIncassato } = modello;
  const storico = raggruppaPerFattura(pagamenti);

  const handleDelete = async (pagamentoId) => {
    setDeletingId(pagamentoId);
    try {
      const res = await apiFetch(`/clients/${clientId}/pagamenti/${pagamentoId}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Pagamento eliminato");
        onUpdate();
      } else {
        toast.error("Errore nell'eliminazione");
      }
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <SectionCard
      icon={CreditCard}
      iconBg="#f1f1f8"
      iconColor="#565d6d"
      title="Pagamenti"
      count={haPacchetto ? `${paidCount}/${items.length} pagati` : null}
      testId="pagamenti-section"
      action={
        <span className="inline-flex items-center gap-3">
          <SenzaFatturaPill client={client} clientId={clientId} onUpdate={onUpdate} />
          {haPacchetto && <StatBlock label="Da incassare" value={formatCurrency(daIncassare, { decimals: 0 })} color={tuttoIncassato ? "#0f7040" : "#8a6414"} />}
        </span>
      }
    >
      <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <ScadenzaPagamenti client={client} clientId={clientId} user={user} onUpdate={onUpdate} />
        <FatturaMese client={client} clientId={clientId} user={user} meseBackend={meseBackend} onUpdate={onUpdate} />
      </div>
      <BloccoArretrati
        arretrati={arretrati}
        clientId={clientId}
        canRegister={canRegister}
        onRegistrato={(risposta, mese) => { avvisaRegistrato(risposta, mese); onUpdate(); }}
      />
      {!haPacchetto ? (
        <p className="text-sm text-muted-foreground italic">Nessun pacchetto con attivazione o addon associato a questo cliente</p>
      ) : (
        <RegistroPagamenti
          modello={modello}
          clientId={clientId}
          pagamenti={pagamenti}
          canRegister={canRegister}
          onRegistrato={onUpdate}
          onCambiaPct={puoModificarePct(user) && quotaPercentuale ? async (pct) => {
            await salvaPctMese(clientId, quotaPercentuale.mese_fatturato, pct);
            onUpdate();
            onPctCambiata?.();
          } : undefined}
        />
      )}

      <MesiSaltati client={client} clientId={clientId} canSkip={canSkip} onUpdate={onUpdate} />

      {(pagamenti.length > 0 || (canSkip && modello.rigaCanone)) && (
        <div className="mt-3 pt-3 border-t border-[#f0f0f6]">
          <div className="flex items-center gap-2 flex-wrap">
            {pagamenti.length > 0 && (
              <button className={GHOST_BTN} onClick={() => setShowStorico(s => !s)}>
                {showStorico ? "Nascondi" : "Mostra"} storico pagamenti ({storico.length})
              </button>
            )}
            <div className="flex-1" />
            {canSkip && modello.rigaCanone && (
              <button className={DANGER_BTN} onClick={() => setShowSalta(true)} data-testid="salta-mensile-btn">
                <CalendarOff className="w-3.5 h-3.5" /> Salta un mensile
              </button>
            )}
          </div>
          {showStorico && (
            <div className="mt-2 space-y-1 max-h-48 overflow-y-auto" data-testid="pagamenti-storico">
              {storico.map(p => (
                <div key={p.pagamento_id} className="flex items-center justify-between px-3 py-2 rounded-[10px] hover:bg-[#fbfbfd] text-[13px]">
                  <div className="flex flex-col">
                    <span className="text-[#3c414d]">{p.label}{p.periodo ? ` — ${new Date(p.periodo + "-01").toLocaleDateString("it-IT", { month: "long", year: "numeric" })}` : ""}</span>
                    {p.voci && <span className="text-[11.5px] text-[#9095a1]">{p.voci.join(" · ")}</span>}
                    <span className="text-[11.5px] text-[#9095a1] scheda-font-mono">{new Date(p.data_pagamento).toLocaleDateString("it-IT")}{p.note ? ` · ${p.note}` : ""}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold scheda-font-mono text-[#15161c]">{formatCurrency(p.importo, { decimals: 0 })}</span>
                    {canRegister && <button
                      className="h-6 w-6 rounded-[8px] flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40"
                      onClick={() => handleDelete(p.pagamento_id)}
                      disabled={deletingId === p.pagamento_id}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {canSkip && (
        <SaltaMensileDialog
          open={showSalta}
          onOpenChange={setShowSalta}
          client={client}
          clientId={clientId}
          meseIniziale={modello.meseCorrente}
          onSaltato={() => onUpdate()}
        />
      )}
    </SectionCard>
  );
}

// Registrazione rapida dalla lista clienti: le stesse righe della sezione
// Pagamenti della scheda, senza doverla aprire.
//
// Deve sembrare immediata anche con la latenza di produzione:
// - legge GET /clients/{id}/pagamenti (pacchetti, pagamenti, stato del mese),
//   non l'intera scheda; la lista la precarica al passaggio sul tag e la tiene
//   in cache, così all'apertura i dati ci sono già e si riallineano in background;
// - dopo una registrazione il backend restituisce lo stato aggiornato: la
//   modale e la riga della lista si aggiornano da lì, senza ricaricare la lista.
// I clienti senza servizi non hanno righe attese: per loro "pagato" resta
// l'interruttore manuale di PATCH /clients/{id}/admin, e la modale lo offre.
const cachePagamenti = new Map();

async function leggiPagamenti(clientId) {
  const res = await apiFetch(`/clients/${clientId}/pagamenti`);
  if (!res.ok) throw new Error(String(res.status));
  const dati = await res.json();
  cachePagamenti.set(clientId, dati);
  return dati;
}

export function precaricaPagamenti(clientId) {
  if (clientId && !cachePagamenti.has(clientId)) leggiPagamenti(clientId).catch(() => {});
}

// Apertura dall'esterno senza stato nella pagina: se la lista tenesse il
// cliente aperto nel proprio stato, toccare il tag ridisegnerebbe tutte le sue
// righe (centinaia) prima di mostrare la modale.
let apriModale = null;
export function apriRegistraPagamenti(clientId) {
  if (apriModale) apriModale(clientId);
}

export function ModaleRegistraPagamenti({ user, onAggiornaCliente }) {
  const navigate = useNavigate();
  const [clientId, setClientId] = useState(null);
  const onClose = () => setClientId(null);
  useEffect(() => {
    apriModale = setClientId;
    return () => { if (apriModale === setClientId) apriModale = null; };
  }, []);
  const [dati, setDati] = useState(null);
  const [errore, setErrore] = useState(false);
  const [savingManuale, setSavingManuale] = useState(false);

  useEffect(() => {
    if (!clientId) return undefined;
    let attivo = true;
    setDati(cachePagamenti.get(clientId) || null);
    setErrore(false);
    leggiPagamenti(clientId)
      .then(d => { if (attivo) setDati(d); })
      .catch(() => { if (attivo && !cachePagamenti.has(clientId)) setErrore(true); });
    return () => { attivo = false; };
  }, [clientId]);

  // Applica la risposta di una scrittura: nuovi pagamenti, stato del mese e
  // "pagato", in modale, in cache e sulla riga della lista.
  const applica = (nuovi, esito) => {
    setDati(prev => {
      if (!prev) return prev;
      const aggiornato = {
        ...prev,
        items: [...nuovi, ...prev.items],
        stato_pagamenti: esito.stato_pagamenti || prev.stato_pagamenti,
        client: { ...prev.client, ...(esito.ha_pagato !== undefined ? { ha_pagato: esito.ha_pagato } : {}) },
      };
      cachePagamenti.set(clientId, aggiornato);
      return aggiornato;
    });
    const patch = {};
    if (esito.stato_pagamenti) patch.stato_pagamenti = esito.stato_pagamenti;
    if (esito.ha_pagato !== undefined && esito.ha_pagato !== null) patch.ha_pagato = esito.ha_pagato;
    if (Object.keys(patch).length) onAggiornaCliente(clientId, patch);
  };

  const onRegistrato = (risposta) => {
    const nuovi = risposta.items || (risposta.pagamento_id ? [risposta] : []);
    applica(nuovi, risposta);
  };

  const client = dati?.client;
  const pagamenti = dati?.items || [];
  const meseBackend = dati?.stato_pagamenti?.mese_corrente?.mese;
  const quotaMese = dati?.stato_pagamenti?.mese_corrente?.quota_percentuale;
  const modello = client ? modelloPagamenti(client, pagamenti, meseBackend, quotaMese) : null;
  const gestito = !!dati?.stato_pagamenti?.gestito;
  const pagatoManuale = client?.ha_pagato !== false;
  const nomeMese = new Date((meseBackend || new Date().toISOString().slice(0, 7)) + "-01")
    .toLocaleDateString("it-IT", { month: "long", year: "numeric" });

  const impostaPagatoManuale = async (valore) => {
    setSavingManuale(true);
    try {
      const res = await apiFetch(`/clients/${clientId}/admin`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ha_pagato: valore }),
      });
      if (res.ok) {
        toast.success(valore ? "Segnato come pagato" : "Segnato come non pagato");
        applica([], { ha_pagato: valore });
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Errore nel salvataggio");
      }
    } finally {
      setSavingManuale(false);
    }
  };

  return (
    <Dialog open={!!clientId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className={`${DIALOG_CONTENT_CLS} max-w-[calc(100vw-32px)] sm:max-w-2xl max-h-[88vh] overflow-y-auto gap-0`}
        data-testid="registra-pagamenti-dialog"
      >
        <div className="flex items-start gap-3 pr-8">
          <span className="w-9 h-9 rounded-[11px] bg-[#f1f1f8] text-[#565d6d] flex items-center justify-center shrink-0">
            <CreditCard className="w-[18px] h-[18px]" strokeWidth={2} />
          </span>
          <div className="flex-1 min-w-0">
            <DialogTitle className="text-[16px] font-bold text-[#15161c] truncate">
              Pagamenti{client?.name ? ` · ${client.name}` : ""}
            </DialogTitle>
            <DialogDescription className="text-[13px] text-[#9095a1] mt-0.5">
              {modello?.haPacchetto
                ? `${nomeMese.charAt(0).toUpperCase()}${nomeMese.slice(1)} · ${modello.paidCount}/${modello.items.length} pagati`
                : `Stato di ${nomeMese}`}
            </DialogDescription>
          </div>
          {modello?.haPacchetto && (
            <StatBlock
              label="Da incassare"
              value={formatCurrency(modello.daIncassare, { decimals: 0 })}
              color={modello.tuttoIncassato ? "#0f7040" : "#8a6414"}
            />
          )}
        </div>

        <div className="mt-4">
          {errore ? (
            <p className="text-[13px] text-[#c0392b]">Impossibile caricare i pagamenti del cliente.</p>
          ) : !dati ? (
            // Stessa altezza di una riga: all'arrivo dei dati la modale non salta.
            <div className="h-[66px] rounded-[14px] border border-[#e9e9f1] bg-[#fbfbfd] animate-pulse" />
          ) : gestito && modello.haPacchetto ? (
            <>
            <BloccoArretrati
              arretrati={dati.arretrati_dettaglio}
              clientId={clientId}
              canRegister={puoRegistrarePagamenti(user)}
              onRegistrato={(risposta, mese) => {
                avvisaRegistrato(risposta, mese);
                // Gli arretrati si ricalcolano sul backend: si rilegge il cliente.
                applica([], risposta);
                leggiPagamenti(clientId).then(setDati).catch(() => {});
              }}
            />
            <RegistroPagamenti
              modello={modello}
              clientId={clientId}
              pagamenti={pagamenti}
              canRegister={puoRegistrarePagamenti(user)}
              onRegistrato={onRegistrato}
              onCambiaPct={puoModificarePct(user) && quotaMese ? async (pct) => {
                applica([], await salvaPctMese(clientId, quotaMese.mese_fatturato, pct));
              } : undefined}
              rapido
            />
            </>
          ) : (
            <div className="flex items-center gap-3 flex-wrap px-[18px] py-[15px] rounded-[14px] border border-[#e9e9f1]" data-testid="pagato-manuale">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: pagatoManuale ? "#12a05c" : "#e05a4a" }} />
              <div className="flex-1 min-w-[180px]">
                <div className="text-[14.5px] font-semibold text-[#15161c]">{pagatoManuale ? "Pagato" : "Non pagato"}</div>
                <div className="text-[12.5px] text-[#6b7280]">
                  Cliente senza servizi nel registro pagamenti: lo stato del mese si imposta a mano.
                </div>
              </div>
              <button
                className={pagatoManuale ? SECONDARY_BTN : PRIMARY_BTN}
                style={{ height: 34 }}
                onClick={() => impostaPagatoManuale(!pagatoManuale)}
                disabled={savingManuale}
                data-testid="pagato-manuale-toggle"
              >
                {pagatoManuale ? "Segna non pagato" : <><CheckCircle2 className="w-3.5 h-3.5" /> Segna pagato</>}
              </button>
            </div>
          )}
        </div>

        <div className="mt-4 flex justify-end">
          <button
            className={GHOST_BTN}
            onClick={() => { onClose(); navigate(`/scheda-cliente/${clientId}`); }}
            data-testid="registra-pagamenti-apri-scheda"
          >
            Apri scheda cliente <ExternalLink className="w-3.5 h-3.5" />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
